import { z } from "zod";
/**
 * GET /api/communities/:id/members — TASK-031 wire contract.
 *
 * The endpoint is a paginated, optionally stage-filtered listing of a
 * community's members enriched with display names from Telegram (cached in
 * Redis with 1h TTL — never persisted, see §15.3). Opted-out and archived
 * Members are silently excluded from BOTH the list AND the per-stage
 * counts (so the FREE-tier "tease" doesn't expose anyone who opted out).
 *
 * FREE plan response: `upgradeRequired=true`, `items=[]`, `nextCursor=null`,
 * `counts` populated. PRO/AGENCY plan response: `upgradeRequired=false`,
 * `items` populated up to `limit`, `nextCursor` non-null when there is a
 * next page, `counts` always populated (used by both the list view header
 * and the FREE-tier tease component without a second round-trip).
 */
export const memberLifecycleStageSchema = z.enum([
    "NEW",
    "ACTIVE",
    "ENGAGED",
    "DORMANT",
    "AT_RISK",
    "CHURNED",
]);
export const MEMBER_LIFECYCLE_STAGES = [
    "NEW",
    "ACTIVE",
    "ENGAGED",
    "DORMANT",
    "AT_RISK",
    "CHURNED",
];
export const MEMBERS_LIST_DEFAULT_LIMIT = 50;
export const MEMBERS_LIST_MAX_LIMIT = 200;
/**
 * Cursor-based pagination key. Order is `lastActiveAt DESC NULLS LAST,
 * id DESC` — `lastActiveAt` may be null on members who joined but never
 * messaged (lurkers). Sorting nulls LAST puts active members first; the
 * tie-breaker on `id` keeps the order deterministic across ties.
 *
 * The wire encoding is base64url(JSON({lastActiveAt: ISO|null, id: string}))
 * — schemas exported here are for the BACKEND parser. Clients treat the
 * cursor as an opaque string and pass it through verbatim.
 */
export const membersListCursorSchema = z.object({
    /** ISO 8601 string OR null (lurker tail of the list). */
    lastActiveAt: z.string().datetime().nullable(),
    /** Member.id — opaque cuid. */
    id: z.string().min(1),
});
/**
 * Query string for the members list. `stage` accepts a comma-separated
 * subset of MemberLifecycleStage; the route splits on commas and validates
 * each part against the enum. Passing an empty string is rejected as a 400
 * (clients should omit the param to mean "all stages") so we don't
 * silently coerce ambiguous input.
 *
 * `cursor` is the opaque base64url string returned in the previous
 * response's `nextCursor`. `limit` is clamped to MEMBERS_LIST_MAX_LIMIT
 * server-side; the schema checks the upper bound here too as a defense.
 */
export const membersListQuerySchema = z
    .object({
    stage: z
        .string()
        .min(1)
        .optional()
        .transform((s) => (s === undefined ? undefined : s.split(",")))
        .pipe(z
        .array(memberLifecycleStageSchema)
        .min(1)
        .max(MEMBER_LIFECYCLE_STAGES.length)
        .optional()),
    cursor: z.string().min(1).optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(MEMBERS_LIST_MAX_LIMIT)
        .optional(),
})
    .strict();
/**
 * One row in the list response. `displayName` is composed from Telegram's
 * `first_name` (+ optional `last_name`) at enrichment time — null when the
 * Bot API getChatMember call fails or the user has been kicked since the
 * Member row was last refreshed. `username` is the public @handle cached on
 * the Member row (may be null — many Telegram users have no @handle).
 *
 * `joinedAt` is IMMUTABLE (per schema.prisma:Member.joinedAt comment).
 * `lastActiveAt` is null for lurkers and members whose only activity is
 * pre-opt-out (which the backfill-delete in §15.5.4 wipes).
 */
export const memberListItemSchema = z.object({
    id: z.string().min(1),
    telegramId: z.string().regex(/^-?\d+$/),
    username: z.string().nullable(),
    displayName: z.string().nullable(),
    lifecycleStage: memberLifecycleStageSchema,
    joinedAt: z.string().datetime(),
    lastActiveAt: z.string().datetime().nullable(),
});
/**
 * Per-stage counts. Always returned (FREE + PRO + AGENCY) so the FREE
 * upgrade tease and the PRO list header use the same payload. Counts
 * exclude opted-out and archived rows — a single audit cohort, never
 * exposing privacy-protected members.
 */
export const memberStageCountsSchema = z.object({
    NEW: z.number().int().nonnegative(),
    ACTIVE: z.number().int().nonnegative(),
    ENGAGED: z.number().int().nonnegative(),
    DORMANT: z.number().int().nonnegative(),
    AT_RISK: z.number().int().nonnegative(),
    CHURNED: z.number().int().nonnegative(),
});
export const membersListResponseSchema = z.object({
    /**
     * FREE plan returns true with `items=[]` + `nextCursor=null`. PRO/AGENCY
     * always returns false. The Mini App branches on this to render the
     * upgrade tease panel without re-querying.
     */
    upgradeRequired: z.boolean(),
    items: z.array(memberListItemSchema),
    nextCursor: z.string().nullable(),
    counts: memberStageCountsSchema,
});
//# sourceMappingURL=members.js.map